import { useCallback, useEffect } from 'react'

import { Flex } from '@react-css/flex'
import Modal from 'react-modal'

import {
  CancelButton,
  ConfirmationButton,
  ConfirmationContent,
  ConfirmationMessage,
} from './styles'

const styles: { overlay: React.CSSProperties; content: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    zIndex: 1000,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  content: {
    position: 'absolute',
    top: '40px',
    left: '40px',
    right: '40px',
    bottom: '40px',
    border: '1px solid #ccc',
    background: '#fff',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    borderRadius: '4px',
    outline: 'none',
    padding: '20px',
    width: '300px',
    maxHeight: '130px',
    margin: 'auto',
  },
}

interface IConfirmationProps {
  isOpen: boolean
  setIsOpen: (v: boolean) => void
  message: string
  content?: JSX.Element
  onConfirm?: () => void
  onCancel?: () => void
  confirmButtonAltText?: string
  cancelButtonAltText?: string
  hideCancelButton?: boolean
  /** When false, caller is responsible for closing after async work. Default true. */
  closeOnConfirm?: boolean
  confirmDisabled?: boolean
  cancelDisabled?: boolean
  styleOverrides?: {
    overlay?: React.CSSProperties
    content?: React.CSSProperties
    confirmationButton?: React.CSSProperties
    cancelButton?: React.CSSProperties
  }
  useKeyboardShortcuts?: boolean
}
export const ConfirmationModal = ({
  isOpen,
  setIsOpen,
  message,
  content,
  onConfirm,
  onCancel,
  confirmButtonAltText = 'Confirm',
  cancelButtonAltText = 'Cancel',
  hideCancelButton,
  closeOnConfirm = true,
  confirmDisabled = false,
  cancelDisabled = false,
  styleOverrides,
  useKeyboardShortcuts = false,
}: IConfirmationProps) => {
  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const confirm = useCallback(() => {
    if (confirmDisabled) return
    if (onConfirm) {
      onConfirm()
    }
    if (closeOnConfirm) {
      closeModal()
    }
  }, [closeModal, closeOnConfirm, confirmDisabled, onConfirm])

  const cancel = useCallback(() => {
    if (cancelDisabled) return
    if (onCancel) {
      onCancel()
    }
    closeModal()
  }, [cancelDisabled, closeModal, onCancel])

  const handleRequestClose = useCallback(() => {
    if (confirmDisabled || cancelDisabled) return
    closeModal()
  }, [cancelDisabled, closeModal, confirmDisabled])

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (useKeyboardShortcuts) {
        const key = e.key

        if (key === 'y') {
          confirm()
        } else if (key === 'n') {
          cancel()
        }
      }
    },
    [cancel, confirm, useKeyboardShortcuts],
  )

  useEffect(() => {
    document.addEventListener('keypress', handleKeyPress)

    return () => {
      document.removeEventListener('keypress', handleKeyPress)
    }
  }, [handleKeyPress, useKeyboardShortcuts])

  const modalStyle: {
    overlay: React.CSSProperties
    content: React.CSSProperties
  } = {
    overlay: { ...styles.overlay, ...styleOverrides?.overlay },
    content: { ...styles.content, ...styleOverrides?.content },
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      contentLabel="Confirmation Modal"
      style={modalStyle}
    >
      <ConfirmationMessage>{message}</ConfirmationMessage>
      {!!content && <ConfirmationContent>{content}</ConfirmationContent>}
      <Flex justifyCenter>
        <ConfirmationButton
          $disabled={confirmDisabled}
          onClick={confirm}
          style={styleOverrides?.confirmationButton}
        >
          {`${confirmButtonAltText} ${useKeyboardShortcuts ? '(Y)' : ''}`}
        </ConfirmationButton>
        {!hideCancelButton && (
          <CancelButton
            $disabled={cancelDisabled}
            onClick={cancel}
            style={styleOverrides?.cancelButton}
          >
            {`${cancelButtonAltText} ${useKeyboardShortcuts ? '(N)' : ''}`}
          </CancelButton>
        )}
      </Flex>
    </Modal>
  )
}
